import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/auth.service';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      await authService.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Reset your password</h1>
        <p style={styles.subtitle}>
          Enter your email and we'll send you a link to reset your password
        </p>

        {error && <div style={styles.error}>{error}</div>}

        {success ? (
          <div style={styles.success}>
            <h3 style={styles.successTitle}>Check your email</h3>
            <p style={styles.successText}>
              If an account exists for {email}, we've sent you a password reset
              link. Please check your inbox and spam folder.
            </p>
            <Link to="/login" style={styles.backButton}>
              Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={styles.input}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isLoading ? styles.buttonDisabled : {}),
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <Link to="/login" style={styles.footerLink}>
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-md)',
    padding: '2rem',
    width: '100%',
    maxWidth: '400px',
  },
  title: {
    color: 'var(--color-deep-purple)',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  subtitle: {
    color: 'var(--color-gray-600)',
    textAlign: 'center',
    marginBottom: '2rem',
    fontSize: '0.9rem',
  },
  error: {
    background: '#fee',
    color: '#c33',
    padding: '0.75rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  success: {
    background: '#efe',
    padding: '1.5rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  successTitle: {
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
  },
  successText: {
    color: 'var(--color-dark-navy)',
    marginBottom: '1rem',
    lineHeight: '1.5',
  },
  backButton: {
    display: 'inline-block',
    background: 'var(--color-warm-orange)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-md)',
    textDecoration: 'none',
    fontWeight: '600',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    color: 'var(--color-dark-navy)',
    fontWeight: '500',
    fontSize: '0.9rem',
  },
  input: {
    padding: '0.75rem',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    transition: 'border-color var(--transition-base)',
  },
  button: {
    background: 'var(--color-warm-orange)',
    color: 'white',
    padding: '0.75rem',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity var(--transition-base)',
    marginTop: '0.5rem',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  footer: {
    marginTop: '1.5rem',
    textAlign: 'center',
  },
  footerLink: {
    color: 'var(--color-soft-blue)',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
};
